import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../lib/rpc';
import { reportDataFetchError } from '../lib/telemetry';

function AudioPlayer({ src, isLight }: { src: string, isLight: boolean }) {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);

    const togglePlay = () => {
        if (!audioRef.current) return;
        if (isPlaying) audioRef.current.pause();
        else audioRef.current.play();
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
        if (!audioRef.current) return;
        setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
    };

    const handleLoadedMetadata = () => {
        if (!audioRef.current) return;
        setDuration(audioRef.current.duration);
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)', padding: '0.5rem 1rem', borderRadius: '24px' }}>
            <audio 
                ref={audioRef} 
                src={src} 
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={() => { setIsPlaying(false); setProgress(0); }}
            />
            <button 
                onClick={togglePlay}
                style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
                {isPlaying ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect></svg>
                ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: '2px' }}><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                )}
            </button>
            <div style={{ flex: 1, height: '4px', background: isLight ? '#e5e7eb' : 'rgba(255,255,255,0.2)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ width: `${progress || 0}%`, height: '100%', background: '#10b981', transition: 'width 0.1s linear' }} />
            </div>
            <div style={{ fontSize: '0.75rem', color: isLight ? '#6b7280' : '#a1a1aa' }}>
                {Math.floor(duration / 60)}:{(Math.floor(duration % 60)).toString().padStart(2, '0')}
            </div>
        </div>
    );
}

export function CrmVoicemails({
    slug,
    isLight,
    client,
    fetchVoicemailsList,
    requirePin
}: {
    slug: string;
    isLight: boolean;
    client: any;
    fetchVoicemailsList: (offset?: number, limit?: number, ext?: string | null) => Promise<any>;
    requirePin: boolean;
}) {
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [unlockMode, setUnlockMode] = useState<'global' | 'extension'>('global');
    const [password, setPassword] = useState('');
    const [extension, setExtension] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [globalAccess, setGlobalAccess] = useState(false);
    const [unlockedExtension, setUnlockedExtension] = useState<string | null>(null);

    const [voicemails, setVoicemails] = useState<any[]>([]);
    const [loadingVoicemails, setLoadingVoicemails] = useState(false);
    
    const [viewMode, setViewMode] = useState<'list' | 'conversational'>('list');
    const [selectedVoicemail, setSelectedVoicemail] = useState<any | null>(null);
    const [selectedCallerId, setSelectedCallerId] = useState<string | null>(null);

    const handleUnlock = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await client.api.v1['crm-dashboard'][':slug'].voicemails.unlock.$post({
                param: { slug },
                json: { password, extension: unlockMode === 'extension' ? extension : undefined }
            });
            const data = await res.json();
            if (data.success) {
                setIsUnlocked(true);
                setGlobalAccess(data.globalAccess);
                setUnlockedExtension(data.extension || null);
                fetchVoicemails(data.globalAccess ? null : data.extension);
            } else {
                setError(data.error || 'Failed to unlock');
            }
        } catch (err: any) {
            setError(err.message || 'Error communicating with server');
        } finally {
            setLoading(false);
        }
    };

    const fetchVoicemails = async (ext: string | null) => {
        setLoadingVoicemails(true);
        try {
            const data = await fetchVoicemailsList(0, 100, ext);
            if (data.success) {
                setVoicemails(data.data || []);
            }
        } catch (e: any) {
            console.error('[Voicemails Fetch Error]', e);
            reportDataFetchError('crm', 'Voicemails', e, { ext });
            setError('Unable to load voicemails at this time.');
        } finally {
            setLoadingVoicemails(false);
        }
    };

    useEffect(() => {
        if (requirePin === false && !isUnlocked) {
            setIsUnlocked(true);
            setGlobalAccess(true);
            fetchVoicemails(null);
        }
    }, [requirePin, isUnlocked]);

    if (!isUnlocked) {
        return (
            <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <div style={{ 
                    background: isLight ? 'white' : '#09090b', 
                    padding: '2rem', 
                    borderRadius: '12px', 
                    border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)',
                    maxWidth: '400px',
                    width: '100%',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                }}>
                    <h2 style={{ marginTop: 0, marginBottom: '1.5rem', textAlign: 'center', color: isLight ? '#09090b' : 'white' }}>Voicemail Access</h2>
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
                        <button
                            onClick={() => setUnlockMode('global')}
                            style={{
                                flex: 1, padding: '0.5rem', borderRadius: '6px',
                                background: unlockMode === 'global' ? '#10b981' : 'transparent',
                                color: unlockMode === 'global' ? 'white' : (isLight ? '#6b7280' : '#a1a1aa'),
                                border: unlockMode === 'global' ? 'none' : (isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.2)'),
                                cursor: 'pointer', fontWeight: 600
                            }}
                        >
                            Global Login
                        </button>
                        <button
                            onClick={() => setUnlockMode('extension')}
                            style={{
                                flex: 1, padding: '0.5rem', borderRadius: '6px',
                                background: unlockMode === 'extension' ? '#10b981' : 'transparent',
                                color: unlockMode === 'extension' ? 'white' : (isLight ? '#6b7280' : '#a1a1aa'),
                                border: unlockMode === 'extension' ? 'none' : (isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.2)'),
                                cursor: 'pointer', fontWeight: 600
                            }}
                        >
                            Extension Login
                        </button>
                    </div>

                    <form onSubmit={handleUnlock} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {unlockMode === 'extension' && (
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.85rem', color: isLight ? '#4b5563' : '#a1a1aa' }}>Extension</label>
                                <input
                                    type="text"
                                    value={extension}
                                    onChange={e => setExtension(e.target.value)}
                                    placeholder="e.g. 101"
                                    required
                                    style={{
                                        width: '100%', padding: '0.75rem', borderRadius: '6px',
                                        background: isLight ? '#f9fafb' : 'rgba(0,0,0,0.5)',
                                        border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)',
                                        color: isLight ? '#09090b' : 'white', boxSizing: 'border-box'
                                    }}
                                />
                            </div>
                        )}
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.85rem', color: isLight ? '#4b5563' : '#a1a1aa' }}>
                                {unlockMode === 'global' ? 'Global Password' : 'PIN'}
                            </label>
                            <input
                                type="password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                placeholder="Enter password/PIN"
                                required
                                style={{
                                    width: '100%', padding: '0.75rem', borderRadius: '6px',
                                    background: isLight ? '#f9fafb' : 'rgba(0,0,0,0.5)',
                                    border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)',
                                    color: isLight ? '#09090b' : 'white', boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        
                        {error && <div style={{ color: '#ef4444', fontSize: '0.85rem', textAlign: 'center' }}>{error}</div>}

                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                width: '100%', padding: '0.75rem', borderRadius: '6px',
                                background: '#10b981', color: 'white', border: 'none',
                                fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                                opacity: loading ? 0.7 : 1, marginTop: '0.5rem'
                            }}
                        >
                            {loading ? 'Authenticating...' : 'Unlock Voicemails'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    const callers = Array.from(new Set(voicemails.map(v => v.callerId || 'Unknown')));
    
    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '1rem', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h2 style={{ margin: 0, color: isLight ? '#09090b' : 'white', fontSize: '1.2rem' }}>
                    Voicemails {globalAccess ? '(Global)' : `(Extension ${unlockedExtension})`}
                </h2>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <div style={{ display: 'flex', background: isLight ? '#e5e7eb' : 'rgba(255,255,255,0.1)', borderRadius: '6px', padding: '2px' }}>
                        <button
                            onClick={() => { setViewMode('list'); setSelectedCallerId(null); }}
                            style={{
                                padding: '0.25rem 0.75rem', borderRadius: '4px', border: 'none', cursor: 'pointer',
                                background: viewMode === 'list' ? (isLight ? 'white' : '#3f3f46') : 'transparent',
                                color: viewMode === 'list' ? (isLight ? '#09090b' : 'white') : (isLight ? '#6b7280' : '#a1a1aa'),
                                boxShadow: viewMode === 'list' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                fontSize: '0.85rem', fontWeight: 500
                            }}
                        >
                            List
                        </button>
                        <button
                            onClick={() => { setViewMode('conversational'); setSelectedVoicemail(null); }}
                            style={{
                                padding: '0.25rem 0.75rem', borderRadius: '4px', border: 'none', cursor: 'pointer',
                                background: viewMode === 'conversational' ? (isLight ? 'white' : '#3f3f46') : 'transparent',
                                color: viewMode === 'conversational' ? (isLight ? '#09090b' : 'white') : (isLight ? '#6b7280' : '#a1a1aa'),
                                boxShadow: viewMode === 'conversational' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                fontSize: '0.85rem', fontWeight: 500
                            }}
                        >
                            Conversations
                        </button>
                    </div>
                    <button
                        onClick={() => { setIsUnlocked(false); setPassword(''); setVoicemails([]); }}
                        style={{
                            padding: '0.25rem 0.75rem', borderRadius: '6px',
                            background: 'transparent', color: isLight ? '#ef4444' : '#f87171',
                            border: isLight ? '1px solid #fca5a5' : '1px solid rgba(248,113,113,0.3)',
                            cursor: 'pointer', fontSize: '0.85rem'
                        }}
                    >
                        Lock
                    </button>
                </div>
            </div>
            
            {loadingVoicemails ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: isLight ? '#6b7280' : '#a1a1aa' }}>
                    Loading voicemails...
                </div>
            ) : viewMode === 'list' ? (
                <div style={{ display: 'flex', flex: 1, gap: '1rem', minHeight: 0 }}>
                    {/* List Left Pane */}
                    <div style={{ 
                        flex: 1, overflowY: 'auto', background: isLight ? 'white' : '#18181b', 
                        borderRadius: '8px', border: isLight ? '1px solid #e5e7eb' : '1px solid #27272a'
                    }}>
                        {voicemails.map(vm => (
                            <div 
                                key={vm.id} 
                                onClick={() => setSelectedVoicemail(vm)}
                                style={{
                                    padding: '1rem', borderBottom: isLight ? '1px solid #e5e7eb' : '1px solid #27272a',
                                    cursor: 'pointer', background: selectedVoicemail?.id === vm.id ? (isLight ? '#f3f4f6' : '#27272a') : 'transparent',
                                    color: isLight ? '#09090b' : 'white'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                    <strong style={{ color: vm.readAt ? (isLight ? '#6b7280' : '#a1a1aa') : undefined }}>{vm.callerId || 'Unknown'}</strong>
                                    <span style={{ fontSize: '0.75rem', color: isLight ? '#6b7280' : '#a1a1aa' }}>{new Date(vm.origTime * 1000).toLocaleDateString()}</span>
                                </div>
                                <div style={{ fontSize: '0.85rem', color: isLight ? '#6b7280' : '#a1a1aa', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {vm.transcriptionText || 'No transcription available...'}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Reading Right Pane */}
                    <div style={{ 
                        flex: 2, display: 'flex', flexDirection: 'column', background: isLight ? 'white' : '#18181b', 
                        borderRadius: '8px', border: isLight ? '1px solid #e5e7eb' : '1px solid #27272a', padding: '1.5rem',
                        overflowY: 'auto'
                    }}>
                        {selectedVoicemail ? (
                            <>
                                <h3 style={{ marginTop: 0, color: isLight ? '#09090b' : 'white' }}>Voicemail from {selectedVoicemail.callerId}</h3>
                                <p style={{ fontSize: '0.85rem', color: isLight ? '#6b7280' : '#a1a1aa', marginBottom: '1.5rem' }}>
                                    Received: {new Date(selectedVoicemail.origTime * 1000).toLocaleString()}<br/>
                                    Extension: {selectedVoicemail.extension}
                                </p>
                                
                                <div style={{ marginBottom: '2rem' }}>
                                    <AudioPlayer 
                                        src={`${API_BASE ? API_BASE.replace(/\/$/, '') : ''}/api/v1/crm-dashboard/${slug}/files/download?key=${encodeURIComponent(selectedVoicemail.r2Key)}&x-crm-password=${encodeURIComponent(localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '')}`}
                                        isLight={isLight} 
                                    />
                                </div>

                                <h4 style={{ margin: '0 0 0.5rem 0', color: isLight ? '#4b5563' : '#d4d4d8' }}>Transcription</h4>
                                <div style={{ background: isLight ? '#f9fafb' : '#09090b', padding: '1rem', borderRadius: '8px', border: isLight ? '1px solid #e5e7eb' : '1px solid #27272a', color: isLight ? '#374151' : '#e4e4e7', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                                    {selectedVoicemail.transcriptionText || <i style={{ opacity: 0.5 }}>Transcription not available.</i>}
                                </div>
                            </>
                        ) : (
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: isLight ? '#9ca3af' : '#71717a' }}>
                                Select a voicemail to read
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div style={{ display: 'flex', flex: 1, gap: '1rem', minHeight: 0 }}>
                    {/* Conversational Left Pane - Callers */}
                    <div style={{ 
                        width: '250px', overflowY: 'auto', background: isLight ? 'white' : '#18181b', 
                        borderRadius: '8px', border: isLight ? '1px solid #e5e7eb' : '1px solid #27272a'
                    }}>
                        {callers.map(caller => (
                            <div 
                                key={caller} 
                                onClick={() => setSelectedCallerId(caller)}
                                style={{
                                    padding: '1rem', borderBottom: isLight ? '1px solid #e5e7eb' : '1px solid #27272a',
                                    cursor: 'pointer', background: selectedCallerId === caller ? (isLight ? '#f3f4f6' : '#27272a') : 'transparent',
                                    color: isLight ? '#09090b' : 'white', fontWeight: 600
                                }}
                            >
                                {caller}
                            </div>
                        ))}
                    </div>

                    {/* Conversational Right Pane - Timeline */}
                    <div style={{ 
                        flex: 1, background: isLight ? 'white' : '#18181b', 
                        borderRadius: '8px', border: isLight ? '1px solid #e5e7eb' : '1px solid #27272a',
                        overflowY: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem'
                    }}>
                        {selectedCallerId ? voicemails.filter(v => v.callerId === selectedCallerId).sort((a,b) => a.origTime - b.origTime).map(vm => (
                            <div key={vm.id} style={{ background: isLight ? '#f9fafb' : '#09090b', padding: '1rem', borderRadius: '12px', border: isLight ? '1px solid #e5e7eb' : '1px solid #27272a' }}>
                                <div style={{ fontSize: '0.8rem', color: isLight ? '#6b7280' : '#a1a1aa', marginBottom: '0.75rem', textAlign: 'center' }}>
                                    {new Date(vm.origTime * 1000).toLocaleString()} (Ext {vm.extension})
                                </div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <AudioPlayer 
                                        src={`${API_BASE ? API_BASE.replace(/\/$/, '') : ''}/api/v1/crm-dashboard/${slug}/files/download?key=${encodeURIComponent(vm.r2Key)}&x-crm-password=${encodeURIComponent(localStorage.getItem(`crm-pw-${slug}`) || sessionStorage.getItem(`crm-pw-${slug}`) || '')}`}
                                        isLight={isLight} 
                                    />
                                </div>
                                <div style={{ color: isLight ? '#374151' : '#e4e4e7', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
                                    {vm.transcriptionText || <i style={{ opacity: 0.5 }}>No transcription.</i>}
                                </div>
                            </div>
                        )) : (
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: isLight ? '#9ca3af' : '#71717a' }}>
                                Select a contact to view voicemail timeline
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
